import { ReactNode } from "react";
import { MdLuggage, MdAirlineSeatReclineNormal, MdOutlineChildCare, MdOutlineEmojiPeople, MdPersonOutline, MdOutlineEmail, MdOutlinePhone } from "react-icons/md";
import { TbToolsKitchen2 } from "react-icons/tb";
import Tag from "src/components/Tag/Tag";
import i18n from "src/i18n";
import { formatDate } from "src/utility/time";
import type { DataMap } from "src/Modules/UserHistory/pages/OrdersList/components/OrderCard/constants";
import { STATUS_MAP } from "src/Modules/UserHistory/constants/OrdersList";
import type { BaggageAddOn, FlightOrderDetails, MealAddOn, Passenger, SeatAddOn } from "src/store/apis/client";

export const flightOrderDataMap: DataMap = {
  orderId: {
    get label() {
      return i18n.t("UserHistory.orderDetails.orderId");
    },
    value: () => null,
  },
  bookingCreationDate: {
    get label() {
      return i18n.t("UserHistory.orderDetails.creationDate");
    },
    value: (val) => (val ? formatDate(val, "YYYY-MM-DD hh:mm A") : "—"),
  },
  pnr: {
    get label() {
      return i18n.t("UserHistory.orderDetails.pnr");
    },
    value: (val) => val ?? "-",
  },
};

export type TabKey = "seats" | "baggages" | "meals";

export const MODAL_TABS: { label: string; icon: ReactNode; key: TabKey }[] = [
  {
    get label() {
      return i18n.t("UserHistory.orderDetails.seats");
    },
    icon: <MdAirlineSeatReclineNormal className="h-full w-full" />,
    key: "seats",
  },
  {
    get label() {
      return i18n.t("UserHistory.orderDetails.baggages");
    },
    icon: <MdLuggage className="h-full w-full" />,
    key: "baggages",
  },
  {
    get label() {
      return i18n.t("UserHistory.orderDetails.meals");
    },
    icon: <TbToolsKitchen2 className="h-full w-full" />,
    key: "meals",
  },
];

export type AddOnSummaryRow = { icon?: ReactNode; label: string; total: number; currency: string };

export type FlightRef = Pick<FlightOrderDetails["segments"][0]["flights"][0], "flightNumber" | "origin" | "destination">;

export type AggregatedMeal = MealAddOn & { quantity: number; totalPrice: number };
export type AggregatedBaggage = BaggageAddOn & { quantity: number; totalPrice: number };
export type AggregatedSeat = SeatAddOn & { quantity: number; totalPrice: number };

export type SSRFlight<T> = { flight: FlightRef; items: T[] };

export type TransformedSegment = Omit<FlightOrderDetails["segments"][0], "flights"> & {
  meals: SSRFlight<AggregatedMeal>[];
  baggages: SSRFlight<AggregatedBaggage>[];
  seats: SSRFlight<AggregatedSeat>[];
};

export type PassengerType = Pick<
  Passenger,
  "firstName" | "lastName" | "title" | "type" | "nationality" | "passportNo" | "isLeadPax" | "email" | "mobile1" | "mobile1CountryCode"
>;

export const PASSENGER_TYPE_ICONS: Record<PassengerType["type"], ReactNode> = {
  Adult: <MdPersonOutline className="size-7" />,
  Child: <MdOutlineEmojiPeople className="size-7" />,
  Infant: <MdOutlineChildCare className="size-7" />,
};

export type PassengerFieldPlacement = "main" | "side" | "bottom";

export type PassengerField = {
  key: keyof PassengerType;
  title?: string;
  icon?: ReactNode;
  placement: PassengerFieldPlacement;
  render: (p: PassengerType) => ReactNode | null;
};

export const PASSENGER_FIELDS: PassengerField[] = [
  {
    get title() {
      return i18n.t("UserHistory.orderDetails.passengerName");
    },
    key: "firstName",
    placement: "main",
    render: (p) => `${p.title} ${p.firstName} ${p.lastName}`,
  },
  {
    key: "isLeadPax",
    placement: "main",
    render: (p) =>
      p.isLeadPax ? (
        <div className="text-font-active rounded-full px-3 py-1 text-xs font-semibold uppercase outline outline-current">
          {i18n.t("UserHistory.orderDetails.mainContact")}
        </div>
      ) : null,
  },
  {
    get title() {
      return i18n.t("UserHistory.orderDetails.type");
    },
    key: "type",
    placement: "side",
    render: (p) => p.type,
  },
  {
    get title() {
      return i18n.t("UserHistory.orderDetails.passportNo");
    },
    key: "passportNo",
    placement: "side",
    render: (p) => p.passportNo || "-",
  },
  {
    key: "email",
    icon: <MdOutlineEmail className="size-4" />,
    placement: "bottom",
    render: (p) => p.email || null,
  },
  {
    key: "mobile1",
    icon: <MdOutlinePhone className="size-4" />,
    placement: "bottom",
    render: (p) => (p.mobile1 ? [p.mobile1CountryCode, p.mobile1].filter(Boolean).join(" ") : null),
  },
];

export type DisplayItem = { label: string; subtitle?: string; quantity: number; price: number; currency: string };

export function getBookingSummaryCardData(data: FlightOrderDetails | undefined) {
  const status = STATUS_MAP[data?.status as keyof typeof STATUS_MAP];
  return {
    data: data as Record<string, any>,
    title: i18n.t("UserHistory.flight.bookingData"),
    orderId: data?.orderId,
    tag: status ? <Tag label={status.label} color={status.color} className="rounded-full p-1 px-5 text-sm outline outline-current" /> : null,
  };
}
